import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "ScreenPipes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Screen Tubes",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Continuous Slot Wedge Wire Screen Pipes",
      lead: "All-welded stainless steel screen pipes and lateral hub distributors engineered with continuous V-wire slots for deep intake wells, media underdrains, and uniform fluid distribution.",
      secondaryCta: {
        label: "Screen Tubes Category",
        to: "/ScreenTubesPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
        alt: "Stainless steel wedge wire screen pipe with continuous slot and support rods",
      },
      cardNote: "Intake & Distribution Media",
      cardLink: {
        label: "Technical Specs",
        to: "/ScreenPipesPage#specifications",
      },
      specs: [
        { label: "Slot Range", value: "0.02 – 5.0 mm" },
        { label: "Slot Tolerance", value: "±0.015 mm" },
        { label: "Standard Materials", value: "AISI 304 / 316L" },
        { label: "Flow Direction", value: "FOTI / FITO Options" },
      ],
    },
    overview: {
      eyebrow: "Well Intake & Underdrain Engineering",
      heading: "High-Collapse Continuous Slot Piping for Uniform Flow",
      paragraphs: [
        "Wedge wire screen pipes feature continuous V-profile surface wires resistance-welded to internal longitudinal support rods. This all-welded structural cage delivers continuous peripheral slot openings that prevent sand, gravel pack, or media beads from entering the pipe while optimizing inflow efficiency.",
        "Unlike perforated or slotted casing, the continuous slot design provides up to three times greater open area, significantly reducing entrance velocity and drawdown. The inward-expanding V-shape prevents sand grains and resin beads from lodging in the apertures, maintaining stable flow over extended operating campaigns.",
        "Manufactured in standard and heavy-duty pipe schedules, our screen pipes can be configured with welded carbon or stainless steel API casing threads, weld rings, blind bottoms, or flanged header hub connections for ion exchange vessels and radial collector wells.",
      ],
      keyPoints: [
        "Continuous slot openings provide significantly higher open intake area than milled casing, lowering entrance velocity and head loss.",
        "Inward-expanding V-wire prevents grain wedging, sand bridging, and slot erosion during high-volume pumping cycles.",
        "Precision fabricated in AISI 304 and 316L stainless steel for dependable corrosion resistance in aggressive groundwater and chemical streams.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
        alt: "Stainless steel wedge wire screen pipe with continuous slot and support rods",
      },
    },
    specTable: {
      heading: "Screen Pipe Engineering Specifications",
      intro: "Standard manufacturing parameters for continuous-slot intake and distribution pipes. Customized pipe schedules, slot sizes, and fittings are built to specification.",
      columns: [
        { key: "parameter", label: "Parameter", align: "left" },
        { key: "range", label: "Standard Range", align: "left" },
        { key: "tolerance", label: "Tolerance", align: "left" },
        { key: "notes", label: "Application Notes", align: "left" },
      ],
      rows: [
        {
          id: "sp-slot",
          parameter: "Slot Width Range",
          range: "0.02 – 5.0 mm",
          tolerance: "±0.015 mm",
          notes: "Maintained uniformly along the entire active intake length",
        },
        {
          id: "sp-diameter",
          parameter: "Nominal Pipe Diameter",
          range: "25 – 600 mm",
          tolerance: "±0.5 mm",
          notes: "Compatible with standard API well casing and pipe flanges",
        },
        {
          id: "sp-length",
          parameter: "Standard Pipe Length",
          range: "500 – 3,000 mm",
          tolerance: "±1.5 mm",
          notes: "Longer strings assembled with precision weld rings or threads",
        },
        {
          id: "sp-materials",
          parameter: "Alloy Grades",
          range: "AISI 304, AISI 316L",
          tolerance: "Standard chemistry",
          notes: "Material test reports available upon order specification",
        },
        {
          id: "sp-fittings",
          parameter: "Connections & End Fittings",
          range: "Weld rings, API casing threads, ANSI/DIN flanges",
          tolerance: "Machined standard",
          notes: "Solid end caps, sump plugs, or reducer cones available",
        },
      ],
      caption: "Values represent verified engineering ranges. Additional configurations engineered to specification.",
    },
    features: {
      heading: "Performance & Construction Features",
      intro: "Optimized for high-volume groundwater extraction, treatment underdrains, and vessel internal distributors.",
      items: [
        {
          title: "Maximized Intake Open Area",
          text: "Continuous helical wrapping creates an uninterrupted slot that maximizes water inlet velocity reduction and prevents drawdown surging.",
        },
        {
          title: "Self-Cleaning Slot Geometry",
          text: "V-profile wires widen inwardly so near-size particulates that pass the exterior aperture flow freely through without plugging.",
        },
        {
          title: "High Tensile & Collapse Strength",
          text: "Heavy longitudinal support rods resist severe compressive loads, tension during installation, and external formation pressures.",
        },
        {
          title: "Versatile Flow Configuration",
          text: "Configurable for outside-in intake in wells or inside-out radial discharge in header-lateral media retention vessels.",
        },
      ],
    },
    related: {
      heading: "Related Cylindrical Wedge Wire Products",
      intro: "Compare screen pipes with candle filter tubes or return to the parent screen tubes engineering hub.",
      cards: [
        {
          title: "Wedge Wire Tubes & Candle Elements",
          text: "All-welded continuous-slot screen tubes and tubular candle elements engineered for high-pressure separation, catalyst retention, and severe solid-liquid straining.",
          to: "/WedgeWireTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
            alt: "Industrial stainless steel wedge wire candle filter element for pressurized vessels",
          },
          badge: "Pressure Vessels",
          linkLabel: "View Wedge Wire Tubes",
        },
        {
          title: "Screen Tubes Category Hub",
          text: "Precision cylindrical screen tubes and all-welded tube assemblies designed for severe differential pressure and high collapse resistance.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Stainless steel wire mesh screen tube cylinder",
          },
          badge: "Parent Category",
          linkLabel: "Back to Screen Tubes",
        },
        {
          title: "Wedge Wire Screen Overview",
          text: "Complete line of flat panels, curved screens, slot cylinders, and specialized wedge wire assemblies for heavy industry.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Overview of stainless steel wedge wire screens",
          },
          badge: "Wedge Wire Family",
          linkLabel: "Explore Wedge Wire",
        },
      ],
    },
    cta: {
      id: "screen-pipes-quote",
      eyebrow: "Custom Screen Pipe Engineering",
      heading: "Request a Technical Quote for Screen Pipes",
      lead: "Provide your required slot width, pipe diameter, segment length, connection threads or weld ends, and operating flow direction for a fast, comprehensive technical proposal.",
      secondaryLabel: "Browse Screen Tubes",
      secondaryTo: "/ScreenTubesPage",
      note: "Material test reports (MTR) and positive material identification (PMI) available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "ScreenPipes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "筛管与滤筒",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "连续缝隙楔形丝滤水筛管与集配水管",
      lead: "全焊式不锈钢连续 V 型丝筛管与集配水母支管，专为深水取水井、水处理树脂底滤及压力容器介质均匀布水而设计。",
      secondaryCta: {
        label: "返回筛管分类",
        to: "/ScreenTubesPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
        alt: "不锈钢锲形丝连续缝隙滤水筛管与支撑筋条结构",
      },
      cardNote: "深井与集配水过滤介质",
      cardLink: {
        label: "技术规格",
        to: "/ScreenPipesPage#specifications",
      },
      specs: [
        { label: "缝隙范围", value: "0.02 – 5.0 mm" },
        { label: "缝隙公差", value: "±0.015 mm" },
        { label: "材质牌号", value: "304L / 316L" },
        { label: "流向形式", value: "外进内出 / 内进外出" },
      ],
    },
    overview: {
      eyebrow: "深井取水与底部配水工程",
      heading: "高抗压溃连续缝隙管道，确保大通量均匀流体分布",
      paragraphs: [
        "互拓生产的楔形丝滤水筛管采用高精度梯形丝全自动电阻熔焊于内部纵向支撑筋条上，形成高刚性圆柱滤管。环向连续缝隙有效阻隔地层泥砂、砾石滤料及树脂颗粒进入管内，同时最大化进水有效通流面积。",
        "与传统冲孔管或割缝管相比，连续 V 型缝隙的开孔率可提高两至三倍，显著降低流体入流速度与沿程水头损失。其向内发散的截面形状彻底消除细砂在缝隙处的嵌塞架桥，保障深井及水处理系统长期高效运转。",
        "产品涵盖外径 25 mm 至 600 mm 的标准管径，两端可配接标准 API 螺纹、对焊短节、盲底管帽或平焊法兰，广泛应用于离子交换柱、活性炭吸附塔及辐射井集配水支管系统。",
      ],
      keyPoints: [
        "连续环绕缝隙结构提供高进水开孔率，降低入口流速，避免涌砂与水头骤降。",
        "向内自释放 V 型槽截面，有效防止颗粒卡堵与局部缝隙冲刷磨损。",
        "选用优质 304L 与 316L 奥氏体不锈钢制造，耐受地下水、盐雾及工业酸碱介质腐蚀。",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
        alt: "不锈钢锲形丝连续缝隙滤水筛管与支撑筋条结构",
      },
    },
    specTable: {
      heading: "滤水筛管工程制造参数",
      intro: "连续缝隙滤水筛管与布水支管标准加工技术规范。管径、壁厚、缝隙精度与接口形式均支持非标按图加工。",
      columns: [
        { key: "parameter", label: "参数项目", align: "left" },
        { key: "range", label: "标准制造范围", align: "left" },
        { key: "tolerance", label: "公差", align: "left" },
        { key: "notes", label: "应用说明", align: "left" },
      ],
      rows: [
        {
          id: "sp-slot",
          parameter: "缝隙宽度范围",
          range: "0.02 – 5.0 mm",
          tolerance: "±0.015 mm",
          notes: "整个过滤段缝隙尺寸高度均匀一致",
        },
        {
          id: "sp-diameter",
          parameter: "公称管道外径",
          range: "25 – 600 mm",
          tolerance: "±0.5 mm",
          notes: "适配标准井管套管接口与管道安装规范",
        },
        {
          id: "sp-length",
          parameter: "标准单管长度",
          range: "500 – 3,000 mm",
          tolerance: "±1.5 mm",
          notes: "长管柱可通过环焊缝平焊或专用螺纹接头对接",
        },
        {
          id: "sp-materials",
          parameter: "不锈钢材质",
          range: "AISI 304, AISI 316L",
          tolerance: "标准成分",
          notes: "按订单要求提供材质检验报告 (MTR)",
        },
        {
          id: "sp-fittings",
          parameter: "连接端头样式",
          range: "对焊环、API 螺纹、法兰接头",
          tolerance: "标准机加工",
          notes: "可选配沉淀管盲盖、吊装提头或变径锥体",
        },
      ],
      caption: "所列数据为确认的制造能力范围。如有特殊工况要求，支持按图定制加工。",
    },
    features: {
      heading: "结构特点与工程性能",
      intro: "专为解决高负荷地下水开采、树脂流失及配水不均工程痛点而设计。",
      items: [
        {
          title: "超大有效进水开孔率",
          text: "螺旋连续缠绕缝隙将有效过流面积最大化，使流体平缓进入，杜绝流速过大导致的泥砂携带。",
        },
        {
          title: "自释放防卡滞几何结构",
          text: "外窄内宽的梯形截面，使穿过表层缝隙的微粒顺畅流走，彻底杜绝孔道卡塞结垢。",
        },
        {
          title: "优异的抗压溃与抗拉强度",
          text: "厚重内部纵向筋条与全电阻焊点使筛管能够承受深井下巨大的地层挤压力与自重悬挂拉力。",
        },
        {
          title: "支持双向流体配置",
          text: "支持深井取水的外进内出模式，亦可定制用于容器母支管内进外出反洗或均匀布水。",
        },
      ],
    },
    related: {
      heading: "相关圆柱形楔形丝产品",
      intro: "了解承压烛式滤芯管或返回筛管分类总览查看全系列产品。",
      cards: [
        {
          title: "楔形丝烛式滤芯与筛管",
          text: "全焊式连续精密缝隙楔形丝滤芯与筛管，专为高压固液分离、催化剂回收及严苛工艺介质截留而设计。",
          to: "/WedgeWireTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
            alt: "用于承压容器的不锈钢全焊式楔形丝烛式滤芯与过滤管",
          },
          badge: "压力容器滤芯",
          linkLabel: "查看烛式滤芯",
        },
        {
          title: "精密筛管与滤筒分类中心",
          text: "采用定制加工不锈钢楔形截面圆筒结构，适用于强腐蚀性流体与高压工况过滤的筛管产品矩阵。",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "不锈钢编织及烧结丝网精密滤筒筛管",
          },
          badge: "上一级分类",
          linkLabel: "返回筛管分类",
        },
        {
          title: "楔形丝筛网总览",
          text: "平面筛板、弧形筛面、圆柱筛筒以及工业滤帽等全系列全焊式楔形丝过滤产品总览。",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "楔形丝筛网产品总览",
          },
          badge: "产品族总览",
          linkLabel: "查看楔形丝总览",
        },
      ],
    },
    cta: {
      id: "screen-pipes-quote",
      eyebrow: "滤水筛管方案定制",
      heading: "获取连续缝隙滤水筛管技术报价",
      lead: "请提供所需的缝隙宽度、管道直径、单支长度、连接方式及流向要求，我们的技术团队将快速为您出具详细图纸与报价方案。",
      secondaryLabel: "返回筛管产品中心",
      secondaryTo: "/ScreenTubesPage",
      note: "按订单要求可提供材质检验报告 (MTR) 与 PMI 合金成分分析报告。",
    },
  },
  es: {
    breadcrumbKey: "ScreenPipes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Tubos de Filtración",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tuberías Ranuradas de Alambre en Cuña Continuo",
      lead: "Tuberías ranuradas de acero inoxidable totalmente soldadas y colectores laterales diseñados con hilo en V continuo para captación en pozos profundos y distribución uniforme de fluidos.",
      secondaryCta: {
        label: "Categoría Tubos Filtrantes",
        to: "/ScreenTubesPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
        alt: "Tubo de criba de alambre de cuña de acero inoxidable con ranura continua y varillas de soporte",
      },
      cardNote: "Medios de Captación y Drenaje",
      cardLink: {
        label: "Datos Técnicos",
        to: "/ScreenPipesPage#specifications",
      },
      specs: [
        { label: "Rango de ranura", value: "0,02 – 5,0 mm" },
        { label: "Tolerancia", value: "±0,015 mm" },
        { label: "Materiales", value: "304L / 316L" },
        { label: "Dirección de flujo", value: "FOTI / FITO" },
      ],
    },
    overview: {
      eyebrow: "Captación en Pozos y Drenaje de Lecho",
      heading: "Tuberías de Alta Resistencia al Colapso para Caudales Elevados",
      paragraphs: [
        "Las tuberías ranuradas en alambre en cuña combinan un alambre superficial de perfil en V soldado por fusión sobre varillas longitudinales internas. Esta estructura monolítica proporciona ranuras continuas que impiden el paso de arenas o resinas mientras optimizan el caudal.",
        "A diferencia de las tuberías perforadas o ranuradas convencionales, la ranura continua ofrece hasta tres veces más área abierta, reduciendo la velocidad de entrada del fluido y las pérdidas de carga. El perfil en V ensanchado hacia el interior evita el atascamiento de partículas.",
        "Fabricadas en diámetros de 25 mm a 600 mm, admiten conexiones roscadas API, anillos para soldar o bridas normalizadas para su acoplamiento en pozos o tanques de tratamiento.",
      ],
      keyPoints: [
        "Abertura continua que ofrece una mayor área de paso, reduciendo drásticamente la velocidad de entrada y el arrastre de finos.",
        "Perfil en V autolimpiante que previene la colmatación y el bloqueo por partículas de tamaño similar a la ranura.",
        "Construcción en AISI 304L y 316L para una excelente resistencia a la corrosión en aguas subterráneas o fluidos agresivos.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
        alt: "Tubo de criba de alambre de cuña de acero inoxidable con ranura continua y varillas de soporte",
      },
    },
    specTable: {
      heading: "Especificaciones Técnicas de Tuberías Ranuradas",
      intro: "Parámetros habituales de fabricación para tuberías de captación y distribución. Dimensiones especiales fabricadas bajo plano.",
      columns: [
        { key: "parameter", label: "Parámetro", align: "left" },
        { key: "range", label: "Rango Estándar", align: "left" },
        { key: "tolerance", label: "Tolerancia", align: "left" },
        { key: "notes", label: "Notas de Aplicación", align: "left" },
      ],
      rows: [
        {
          id: "sp-slot",
          parameter: "Luz de ranura",
          range: "0,02 – 5,0 mm",
          tolerance: "±0,015 mm",
          notes: "Separación continua a lo largo del tramo filtrante",
        },
        {
          id: "sp-diameter",
          parameter: "Diámetro nominal",
          range: "25 – 600 mm",
          tolerance: "±0,5 mm",
          notes: "Compatible con tubería estándar y normas de pozo",
        },
        {
          id: "sp-length",
          parameter: "Longitud unitaria",
          range: "500 – 3.000 mm",
          tolerance: "±1,5 mm",
          notes: "Unión de columnas largas mediante rosca o soldadura",
        },
        {
          id: "sp-materials",
          parameter: "Aleaciones",
          range: "AISI 304L, AISI 316L",
          tolerance: "Composición estándar",
          notes: "Certificados de material disponibles bajo pedido",
        },
        {
          id: "sp-fittings",
          parameter: "Conexiones de extremo",
          range: "Roscas API, Anillos a soldar, Bridas ANSI/DIN",
          tolerance: "Mecanizado normalizado",
          notes: "Disponibilidad de fondos ciegos o tapones cónicos",
        },
      ],
      caption: "Rangos verificados de fabricación. Opciones adicionales bajo requerimiento técnico.",
    },
    features: {
      heading: "Prestaciones y Características de Diseño",
      intro: "Desarrolladas para extracción intensiva de agua y distribución en lechos de resina y arena.",
      items: [
        {
          title: "Máxima Área Abierta",
          text: "El bobinado helicoidal ininterrumpido reduce la velocidad de entrada del agua y minimiza la turbulencia.",
        },
        {
          title: "Geometría Antiatascamiento",
          text: "El diseño en V se ensancha hacia el interior, facilitando el paso sin trabas de las partículas que cruzan la ranura.",
        },
        {
          title: "Alta Resistencia al Colapso",
          text: "Varillas de soporte robustas que soportan grandes presiones de formación y tracción durante la instalación.",
        },
        {
          title: "Flujo Reversible",
          text: "Apta para captación (flujo exterior a interior) o para lavado contracorriente y distribución en tanques.",
        },
      ],
    },
    related: {
      heading: "Productos Cilíndricos Relacionados",
      intro: "Compare las tuberías ranuradas con elementos tipo bujía o regrese al centro de tubos filtrantes.",
      cards: [
        {
          title: "Tubos de Alambre Cuña y Elementos Tipo Bujía",
          text: "Tubos de ranura continua y elementos tipo bujía totalmente soldados para separación a alta presión y recuperación de catalizadores.",
          to: "/WedgeWireTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
            alt: "Elemento filtrante de tubo de alambre cuña de acero inoxidable para recipientes a presión",
          },
          badge: "Recipientes a Presión",
          linkLabel: "Ver Elementos Tipo Bujía",
        },
        {
          title: "Centro de Tubos Filtrantes",
          text: "Cilindros de malla y perfiles de cuña totalmente soldados, diseñados para resistir presiones severas.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Cilindro de tubo filtrante de malla de alambre de acero inoxidable",
          },
          badge: "Categoría Superior",
          linkLabel: "Volver a Tubos Filtrantes",
        },
        {
          title: "Gama de Alambre en Cuña",
          text: "Catálogo completo de paneles planos, rejillas curvadas y cilindros filtrantes para procesos industriales.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Resumen de productos de alambre en cuña",
          },
          badge: "Gama de Productos",
          linkLabel: "Explorar Alambre en Cuña",
        },
      ],
    },
    cta: {
      id: "screen-pipes-quote",
      eyebrow: "Fabricación a Medida",
      heading: "Solicite Oferta Técnica para Tuberías Ranuradas",
      lead: "Indique la luz de ranura, diámetro, longitud de tramo y tipo de conexión para recibir una cotización detallada.",
      secondaryLabel: "Ver Tubos Filtrantes",
      secondaryTo: "/ScreenTubesPage",
      note: "Certificados de ensayo de material (MTR) y análisis PMI disponibles bajo requerimiento de pedido.",
    },
  },
  de: {
    breadcrumbKey: "ScreenPipes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Filterrohre",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Spaltsiebrohre & Sternverteilersysteme",
      lead: "Vollverschweißte Edelstahl-Spaltsiebrohre mit kontinuierlichem V-Profildraht für Tiefbrunnen, Harzfänger und gleichmäßige Medienverteilung in Druckbehältern.",
      secondaryCta: {
        label: "Filterrohre Übersicht",
        to: "/ScreenTubesPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
        alt: "Spaltrohr aus Edelstahl mit kontinuierlicher Spaltweite und Längstragstäben",
      },
      cardNote: "Brunnenfilter & Verteilersysteme",
      cardLink: {
        label: "Technische Daten",
        to: "/ScreenPipesPage#specifications",
      },
      specs: [
        { label: "Spaltweite", value: "0,02 – 5,0 mm" },
        { label: "Toleranz", value: "±0,015 mm" },
        { label: "Werkstoffe", value: "304L / 316L" },
        { label: "Strömung", value: "FOTI / FITO" },
      ],
    },
    overview: {
      eyebrow: "Brunnenbau & Medienverteilung",
      heading: "Kollapsfeste Endlosspaltrohre für homogene Durchströmung",
      paragraphs: [
        "Spaltsiebrohre zeichnen sich durch kontinuierliche V-Profildrähte aus, die auf innenliegende Längstragstäbe widerstandsgeschweißt sind. Diese Bauweise verhindert zuverlässig das Eindringen von Sand oder Filtermedien und maximiert den Durchfluss.",
        "Gegenüber Loch- oder Schlitzrohren bieten Spaltsiebrohre eine bis zu dreifach größere freie Einströmfläche, wodurch Eintrittsgeschwindigkeiten und Druckverluste minimiert werden. Das sich nach innen erweiternde V-Profil verhindert das Festsetzen von Partikeln.",
        "Erhältlich in Durchmessern von 25 mm bis 600 mm, ausgestattet mit API-Gewinden, Anschweißringen oder Flanschen für Ionenaustauscher und Brunnenfilterstränge.",
      ],
      keyPoints: [
        "Kontinuierliche Spalte bieten maximale Einströmfläche, reduzieren Turbulenzen und verhindern Sandeintrag.",
        "Verstopfungsfreies V-Profil verhindert Brückenbildung und Partikelverkeilung.",
        "Gefertigt aus korrosionsbeständigem 304L und 316L für lange Standzeiten in aggressiven Medien.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
        alt: "Spaltrohr aus Edelstahl mit kontinuierlicher Spaltweite und Längstragstäben",
      },
    },
    specTable: {
      heading: "Technische Parameter für Spaltsiebrohre",
      intro: "Standardabmessungen für Brunnen- und Verteilerrohre. Sonderdurchmesser und Anschlüsse werden kundenspezifisch gefertigt.",
      columns: [
        { key: "parameter", label: "Parameter", align: "left" },
        { key: "range", label: "Standardbereich", align: "left" },
        { key: "tolerance", label: "Toleranz", align: "left" },
        { key: "notes", label: "Hinweise", align: "left" },
      ],
      rows: [
        {
          id: "sp-slot",
          parameter: "Spaltweite",
          range: "0,02 – 5,0 mm",
          tolerance: "±0,015 mm",
          notes: "Exakt eingehaltene Spaltweite über die gesamte Filterstrecke",
        },
        {
          id: "sp-diameter",
          parameter: "Nennrohrdurchmesser",
          range: "25 – 600 mm",
          tolerance: "±0,5 mm",
          notes: "Passend für Standard-Brunnenbau- und Behälterverrohrungen",
        },
        {
          id: "sp-length",
          parameter: "Einzellänge",
          range: "500 – 3.000 mm",
          tolerance: "±1,5 mm",
          notes: "Längere Stränge werden durch Schweißringe oder Gewinde verbunden",
        },
        {
          id: "sp-materials",
          parameter: "Werkstoffe",
          range: "AISI 304L, AISI 316L",
          tolerance: "Standardanalyse",
          notes: "Abnahmeprüfzeugnisse auf Wunsch bei Bestellung",
        },
        {
          id: "sp-fittings",
          parameter: "Endanschlüsse",
          range: "Schweißringe, API-Gewinde, Flansche",
          tolerance: "Präzisionsgefertigt",
          notes: "Bodenkappen, Sumpfrohre und Übergangsstücke lieferbar",
        },
      ],
      caption: "Bestätigte Fertigungsmaße. Sonderanfertigungen auf Anfrage.",
    },
    features: {
      heading: "Konstruktionsmerkmale & Einsatzvorteile",
      intro: "Optimiert für hohe Förderraten im Brunnenbau und zuverlässigen Medienrückhalt.",
      items: [
        {
          title: "Hohe Freie Einströmfläche",
          text: "Die kontinuierliche Wicklung maximiert den Durchfluss und verhindert das Ansaugen von Sedimenten.",
        },
        {
          title: "Selbstreinigendes Spaltprofil",
          text: "Partikel, die den Außenspalt passieren, werden durch die interne Aufweitung ohne Verstopfung durchgeleitet.",
        },
        {
          title: "Hohe Kollaps- und Zugfestigkeit",
          text: "Massive Längstragstäbe halten hohen Schüttungsdrücken und Zugkräften beim Einbau stand.",
        },
        {
          title: "Vielseitige Strömungsführung",
          text: "Auslegbar für Einströmung von außen nach innen oder radiale Ausströmung von innen nach außen.",
        },
      ],
    },
    related: {
      heading: "Passende Zylindrische Spaltsiebe",
      intro: "Vergleichen Sie Filterrohre mit Spaltfilterkerzen oder kehren Sie zur Kategorie Filterrohre zurück.",
      cards: [
        {
          title: "Spaltrohre & Spaltfilterelemente",
          text: "Vollverschweißte Endlosspalt-Spaltrohre und Filterkerzen für Hochdrucktrennung, Katalysatorrückgewinnung und anspruchsvolle Filtration.",
          to: "/WedgeWireTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
            alt: "Industrielles Spaltrohr-Filterelement aus Edelstahl für Druckbehälter",
          },
          badge: "Druckbehälter",
          linkLabel: "Spaltfilterkerzen ansehen",
        },
        {
          title: "Kategorie Filterrohre",
          text: "Präzisionsgefertigte Maschenzylinder und Spaltrohre aus Edelstahl für anspruchsvolle Filtrationsaufgaben.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Edelstahl-Drahtgewebe-Filterrohrzylinder",
          },
          badge: "Übergeordnete Kategorie",
          linkLabel: "Zurück zu Filterrohre",
        },
        {
          title: "Spaltsiebe Gesamtübersicht",
          text: "Flachsiebe, Bogensiebe, Zylinder und Düsen für anspruchsvolle industrielle Trennprozesse.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Übersicht Edelstahl-Spaltsiebe",
          },
          badge: "Produktfamilie",
          linkLabel: "Spaltsiebe entdecken",
        },
      ],
    },
    cta: {
      id: "screen-pipes-quote",
      eyebrow: "Maßgefertigte Filterrohre",
      heading: "Technisches Angebot für Spaltsiebrohre anfordern",
      lead: "Nennen Sie uns Spaltweite, Rohrdurchmesser, Teillänge und Anschlussart für ein detailliertes Angebot.",
      secondaryLabel: "Filterrohre anzeigen",
      secondaryTo: "/ScreenTubesPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Analysen bei entsprechender Bestellangabe verfügbar.",
    },
  },
  fr: {
    breadcrumbKey: "ScreenPipes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Tubes Filtrants",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tubes Crépines et Collecteurs Latéraux en Fil Profilé",
      lead: "Tubes crépines et rampes de distribution en acier inoxydable mécano-soudé, conçus avec une fente continue en V pour puits de captage, crépines de fond et distribution uniforme des fluides.",
      secondaryCta: {
        label: "Catégorie Tubes Filtrants",
        to: "/ScreenTubesPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
        alt: "Tube crépine en acier inoxydable à fente continue et tiges de support",
      },
      cardNote: "Captage d'Eau & Distribution",
      cardLink: {
        label: "Spécifications",
        to: "/ScreenPipesPage#specifications",
      },
      specs: [
        { label: "Plage d'ouverture", value: "0,02 – 5,0 mm" },
        { label: "Tolérance", value: "±0,015 mm" },
        { label: "Matériaux", value: "304L / 316L" },
        { label: "Sens d'écoulement", value: "FOTI / FITO" },
      ],
    },
    overview: {
      eyebrow: "Captage en Puits Profonds et Drains de Fond",
      heading: "Crépines Tubulaires Haute Résistance pour Débits Élevés",
      paragraphs: [
        "Les tubes crépines en fil profilé associent un fil continu en V soudé par fusion sur des tiges longitudinales axiales. Cette cage rigide procure des fentes circonférentielles ininterrompues qui retiennent le sable et les résines tout en optimisant le débit.",
        "Comparées aux tubes perforés ou frainés, les crépines continues offrent jusqu'à trois fois plus de surface ouverte, réduisant la vitesse d'admission et les pertes de charge. Le profil s'élargissant vers l'intérieur empêche tout coincement de grains.",
        "Fabriquées du diamètre 25 mm au 600 mm, elles reçoivent des raccords filetés API, embouts lisses à souder ou brides pour installation dans les puits ou cuves industrielles.",
      ],
      keyPoints: [
        "Fente continue maximisant la surface d'admission et réduisant les vitesses d'entrée pour éviter l'érosion.",
        "Profil en V autonettoyant empêchant l'obstruction par les particules de taille proche de la fente.",
        "Construction en inox 304L et 316L assurant une parfaite tenue face aux eaux souterraines corrosives.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
        alt: "Tube crépine en acier inoxydable à fente continue et tiges de support",
      },
    },
    specTable: {
      heading: "Caractéristiques Techniques des Tubes Crépines",
      intro: "Normes dimensionnelles courantes pour crépines de captage et rampes. Réalisations spéciales selon cahier des charges.",
      columns: [
        { key: "parameter", label: "Paramètre", align: "left" },
        { key: "range", label: "Plage Standard", align: "left" },
        { key: "tolerance", label: "Tolérance", align: "left" },
        { key: "notes", label: "Remarques", align: "left" },
      ],
      rows: [
        {
          id: "sp-slot",
          parameter: "Ouverture de fente",
          range: "0,02 – 5,0 mm",
          tolerance: "±0,015 mm",
          notes: "Écartement parfaitement régulier sur toute la section",
        },
        {
          id: "sp-diameter",
          parameter: "Diamètre nominal",
          range: "25 – 600 mm",
          tolerance: "±0,5 mm",
          notes: "Conforme aux diamètres de tubage standard",
        },
        {
          id: "sp-length",
          parameter: "Longueur standard",
          range: "500 – 3 000 mm",
          tolerance: "±1,5 mm",
          notes: "Colonnes assemblées par filetage ou bagues à souder",
        },
        {
          id: "sp-materials",
          parameter: "Alliages d'acier",
          range: "AISI 304L, AISI 316L",
          tolerance: "Composition conforme",
          notes: "Certificats matière disponibles sur demande",
        },
        {
          id: "sp-fittings",
          parameter: "Raccordements",
          range: "Filetages API, Bagues à souder, Brides",
          tolerance: "Usinage standard",
          notes: "Fonds pleins, bouchons de décantation et cônes disponibles",
        },
      ],
      caption: "Données certifiées de fabrication. Dimensions sur mesure sur demande.",
    },
    features: {
      heading: "Conception et Performances",
      intro: "Conçues pour le captage intensif et la distribution uniforme sans zones mortes.",
      items: [
        {
          title: "Surface d'Admission Maximale",
          text: "L'enroulement hélicoïdal continu permet une entrée d'eau laminaire réduisant le colmatage.",
        },
        {
          title: "Forme en V Non-Colmatante",
          text: "L'élargissement intérieur assure un dégagement immédiat des particules traversant la fente.",
        },
        {
          title: "Haute Résistance Mécanique",
          text: "Les tiges de renfort résistent aux pressions des terrains et aux contraintes de traction au levage.",
        },
        {
          title: "Sens de Flux Réversible",
          text: "Adapté au captage de l'extérieur vers l'intérieur ou à la diffusion inversée dans les réacteurs.",
        },
      ],
    },
    related: {
      heading: "Produits Complémentaires en Fil Profilé",
      intro: "Consultez les bougies filtrantes ou retournez à la catégorie mère des tubes filtrants.",
      cards: [
        {
          title: "Tubes à Fil en Coin et Bougies Filtrantes",
          text: "Tubes à fente continue et éléments bougies soudés conçus pour la séparation haute pression et la récupération de catalyseur.",
          to: "/WedgeWireTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
            alt: "Élément filtrant en fil en coin en acier inoxydable pour cuves sous pression",
          },
          badge: "Cuves sous Pression",
          linkLabel: "Voir les Bougies Filtrantes",
        },
        {
          title: "Catégorie Tubes Filtrants",
          text: "Cylindres en toile métallique et fil en coin conçus pour filtrations exigeantes et forte résistance à l'écrasement.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Tube filtrant cylindrique en toile métallique d'acier inoxydable",
          },
          badge: "Catégorie Parente",
          linkLabel: "Retour aux Tubes Filtrants",
        },
        {
          title: "Gamme Fil en Coin",
          text: "Panneaux plans, grilles courbes et filtres cylindriques pour procédés industriels de séparation.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Vue générale des produits en fil en coin",
          },
          badge: "Famille de Produits",
          linkLabel: "Explorer le Fil en Coin",
        },
      ],
    },
    cta: {
      id: "screen-pipes-quote",
      eyebrow: "Fabrication de Crépines sur Mesure",
      heading: "Demandez une Offre pour Tubes Crépines",
      lead: "Précisez l'ouverture de fente, le diamètre, la longueur et le raccordement pour obtenir une étude chiffrée.",
      secondaryLabel: "Voir les Tubes Filtrants",
      secondaryTo: "/ScreenTubesPage",
      note: "Certificats matière (MTR) et contrôles spectrométriques PMI disponibles sur demande à la commande.",
    },
  },
  pt: {
    breadcrumbKey: "ScreenPipes",
    relatedTopic: "wedge",
    hero: {
      categoryBadge: "Tubos de Filtragem",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Tubos de Peneira e Coletores Laterais em Arame em Cunha",
      lead: "Tubos de peneira e coletores de aço inoxidável totalmente soldados com arame contínuo em V para poços profundos, retenção de leito e distribuição homogênea de fluidos.",
      secondaryCta: {
        label: "Categoria Tubos de Filtragem",
        to: "/ScreenTubesPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
        alt: "Tubo de peneira de arame em cunha de aço inoxidável com ranhura contínua e hastes de suporte",
      },
      cardNote: "Captação e Drenagem",
      cardLink: {
        label: "Especificações",
        to: "/ScreenPipesPage#specifications",
      },
      specs: [
        { label: "Faixa de ranhura", value: "0,02 – 5,0 mm" },
        { label: "Tolerância", value: "±0,015 mm" },
        { label: "Materiais", value: "304L / 316L" },
        { label: "Fluxo", value: "FOTI / FITO" },
      ],
    },
    overview: {
      eyebrow: "Captação em Poços e Drenagem de Meios",
      heading: "Tubulação de Alta Resistência ao Colapso para Vazão Contínua",
      paragraphs: [
        "Os tubos de peneira de arame em cunha combinam perfis contínuos em V soldados por fusão a hastes longitudinais de reforço. Essa estrutura integrada proporciona ranhuras contínuas que bloqueiam a areia e pedregulhos enquanto otimizam o fluxo de captação.",
        "Em comparação com tubos perfurados convencionais, a ranhura helicoidal contínua entrega até três vezes mais área aberta, reduzindo a velocidade de entrada e a perda de carga hidráulica. O alívio interno em V elimina o risco de aprisionamento de grãos.",
        "Produzidos em diâmetros nominais de 25 mm a 600 mm, podem ser fornecidos com roscas API para tubos de revestimento, anéis de solda de topo ou flanges normalizadas para filtros industriais e poços profundos.",
      ],
      keyPoints: [
        "Área de passagem contínua ampliada que reduz a velocidade de admissão e previne arraste de sedimentos.",
        "Perfil em V com desprendimento interno que impede travamento de partículas do tamanho da ranhura.",
        "Fabricação integral em aços 304L e 316L para elevada resistência à corrosão sob águas subterrâneas.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-a9493f.png",
        alt: "Tubo de peneira de arame em cunha de aço inoxidável com ranhura contínua e hastes de suporte",
      },
    },
    specTable: {
      heading: "Parâmetros Construtivos dos Tubos de Peneira",
      intro: "Especificações técnicas padronizadas para tubos de captação e distribuição. Dimensões sob encomenda conforme projeto.",
      columns: [
        { key: "parameter", label: "Parâmetro", align: "left" },
        { key: "range", label: "Faixa Padrão", align: "left" },
        { key: "tolerance", label: "Tolerância", align: "left" },
        { key: "notes", label: "Notas Técnicas", align: "left" },
      ],
      rows: [
        {
          id: "sp-slot",
          parameter: "Abertura da fenda",
          range: "0,02 – 5,0 mm",
          tolerance: "±0,015 mm",
          notes: "Espaçamento uniforme ao longo de toda a extensão útil",
        },
        {
          id: "sp-diameter",
          parameter: "Diâmetro nominal",
          range: "25 – 600 mm",
          tolerance: "±0,5 mm",
          notes: "Compatível com normas usuais de tubulação de poço",
        },
        {
          id: "sp-length",
          parameter: "Comprimento unitário",
          range: "500 – 3.000 mm",
          tolerance: "±1,5 mm",
          notes: "Colunas extensas montadas com anéis de solda ou roscas",
        },
        {
          id: "sp-materials",
          parameter: "Ligas de aço inox",
          range: "AISI 304L, AISI 316L",
          tolerance: "Composição padrão",
          notes: "Relatórios de ensaio de material sob consulta",
        },
        {
          id: "sp-fittings",
          parameter: "Conexões terminais",
          range: "Roscas API, Anéis de solda, Flanges ANSI/DIN",
          tolerance: "Usinagem padrão",
          notes: "Opções de tampão cego, ponteiras e reduções cônicas",
        },
      ],
      caption: "Faixas dimensionais confirmadas. Projetos especiais fornecidos sob especificação.",
    },
    features: {
      heading: "Diferenciais Técnicos e Operação",
      intro: "Desenvolvidos para captação severa de água e distribuição em leitos de resina sem perdas.",
      items: [
        {
          title: "Área Aberta Maximizada",
          text: "O enrolamento contínuo proporciona transição suave de vazão, minimizando turbulências de entrada.",
        },
        {
          title: "Geometria em V Autolimpante",
          text: "Abertura que se alarga para dentro, permitindo que partículas que transpõem a face saiam livremente.",
        },
        {
          title: "Alta Resistência Estrutural",
          text: "Barras longitudinais reforçadas resistem a pressões de formação e esforços de tração na descida do poço.",
        },
        {
          title: "Direção de Fluxo Versátil",
          text: "Adequado para captação de fora para dentro ou distribuição interna de dentro para fora.",
        },
      ],
    },
    related: {
      heading: "Produtos Cilíndricos Complementares",
      intro: "Compare os tubos de peneira com elementos tipo vela ou retorne à categoria geral de tubos filtrantes.",
      cards: [
        {
          title: "Tubos Tipo Cunha e Elementos Vela",
          text: "Tubos de tela perfilada e elementos vela totalmente soldados para separação sob alta pressão e recuperação de catalisadores.",
          to: "/WedgeWireTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/screen_tubes_hero_element.png",
            alt: "Elemento filtrante de tubo de tela tipo cunha em aço inoxidável para vasos pressurizados",
          },
          badge: "Vasos Pressurizados",
          linkLabel: "Ver Elementos Vela",
        },
        {
          title: "Categoria Tubos de Filtragem",
          text: "Cilindros de tela metálica e arame em cunha sob medida para condições exigentes e alta resistência ao colapso.",
          to: "/ScreenTubesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Cilindro de tubo filtrante de malha de aço inoxidável",
          },
          badge: "Categoria Mão",
          linkLabel: "Voltar para Tubos de Filtragem",
        },
        {
          title: "Linha de Arame em Cunha",
          text: "Visão ampla de painéis planos, telas curvas e cilindros filtrantes para a indústria de processo.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stainless-ste-50613e.png",
            alt: "Visão geral de produtos de arame em cunha",
          },
          badge: "Família de Produtos",
          linkLabel: "Explorar Arame em Cunha",
        },
      ],
    },
    cta: {
      id: "screen-pipes-quote",
      eyebrow: "Projetos Sob Medida de Tubos",
      heading: "Solicite uma Cotação para Tubos de Peneira",
      lead: "Informe a abertura de fenda, diâmetro, comprimento e terminações requeridas para receber uma proposta completa.",
      secondaryLabel: "Ver Tubos de Filtragem",
      secondaryTo: "/ScreenTubesPage",
      note: "Relatórios de teste de material (MTR) e ensaio PMI disponíveis quando solicitados no pedido.",
    },
  },
};
